import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { Metric } from '../../components/Metric.js'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { useAuth } from '../../lib/auth.js'
import { formatCount, formatDecimal, formatRate, formatRangeLabel } from '../../lib/format.js'
import { bucketForRange, resolvePeriod, type PeriodLabel } from '../../lib/period.js'
import { usePageTitle } from '../../lib/page-title.js'
import { type TaskListItem, type TaskListPage } from '../manager/useTasks.js'

interface Summary {
  readonly avgEfficiency: number
  readonly avgComplexity: number
  readonly onTimeRate: number
  readonly returnedForCorrection: number
}

interface Department {
  readonly id: string
  readonly name: string
}

interface PersonRow {
  readonly userId: string
  readonly name: string
  readonly departmentId: string | null
  readonly tasks: number
  readonly onTime: number | null
  readonly firstTimePass: number | null
  readonly avgComplexity: number
  readonly score: number | null
  readonly belowMinimumSample: boolean
}

interface DepartmentRow {
  readonly departmentId: string
  readonly name: string
  readonly onTime: number | null
  readonly firstTimePass: number | null
  readonly score: number | null
}

interface TrendPoint {
  readonly from: string
  readonly to: string
  readonly score: number | null
  readonly onTime: number | null
  readonly firstTimePass: number | null
}

interface TrendResponse {
  readonly points: readonly TrendPoint[]
}

interface PersonTaskRow extends TaskListItem {
  readonly firstSubmittedAt?: string | null
  readonly returnCount?: number
}

function sortPeople(rows: readonly PersonRow[]): PersonRow[] {
  return [...rows].sort((a, b) => {
    if (a.belowMinimumSample !== b.belowMinimumSample) return a.belowMinimumSample ? 1 : -1
    if (a.score === null && b.score === null) return a.name.localeCompare(b.name)
    if (a.score === null) return 1
    if (b.score === null) return -1
    if (a.score !== b.score) return b.score - a.score
    return a.name.localeCompare(b.name)
  })
}

function tierLabel(row: PersonRow): string {
  if (row.belowMinimumSample || row.score === null) return 'Not enough data'
  if (row.score >= 85) return 'On track'
  if (row.score >= 75) return 'Steady'
  return 'Needs support'
}

function formatDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return '—'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso))
}

function TeamReport() {
  const { api } = useAuth()
  const [searchParams] = useSearchParams()
  const period = (searchParams.get('period') ?? 'quarter') as PeriodLabel
  const departmentId = searchParams.get('departmentId')
  const { from, to, bucket } = useMemo(() => resolvePeriod(period), [period])
  const scopeSuffix = departmentId === null ? '' : `&departmentId=${encodeURIComponent(departmentId)}`

  const summaryQuery = useQuery({
    queryKey: ['reports-print-summary', from, to, departmentId],
    queryFn: () => api.get<Summary>(`/api/analytics/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${scopeSuffix}`),
  })

  const peopleQuery = useQuery({
    queryKey: ['reports-print-people', from, to, departmentId],
    queryFn: () => api.get<PersonRow[]>(`/api/analytics/people?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${scopeSuffix}`),
  })

  const departmentsQuery = useQuery({
    queryKey: ['reports-print-departments', from, to, departmentId],
    queryFn: () => api.get<DepartmentRow[]>(`/api/analytics/departments?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${scopeSuffix}`),
  })

  const people = useMemo(() => sortPeople(peopleQuery.data ?? []), [peopleQuery.data])
  const selectedPerson = people[0] ?? null

  const trendQuery = useQuery({
    queryKey: ['reports-print-trend', from, to, bucket, selectedPerson?.userId ?? 'none', departmentId],
    enabled: selectedPerson !== null,
    queryFn: () =>
      api.get<TrendResponse>(
        `/api/analytics/trend?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&bucket=${bucket}&assigneeId=${encodeURIComponent(selectedPerson!.userId)}`,
      ),
  })

  const ready =
    summaryQuery.isSuccess &&
    peopleQuery.isSuccess &&
    departmentsQuery.isSuccess &&
    (selectedPerson === null || trendQuery.isSuccess)

  const printed = useRef(false)
  useEffect(() => {
    if (!ready || printed.current) return
    printed.current = true
    window.print()
  }, [ready])

  const failure = summaryQuery.error ?? peopleQuery.error ?? departmentsQuery.error ?? trendQuery.error
  const hasFailure = summaryQuery.isError || peopleQuery.isError || departmentsQuery.isError || trendQuery.isError

  const departments = departmentsQuery.data ?? []
  const summary = summaryQuery.data
  const trend = trendQuery.data?.points ?? []
  const department = departmentId === null ? null : departments.find((item) => item.departmentId === departmentId) ?? null
  const departmentLabel = department?.name ?? departmentId ?? 'All departments'

  if (hasFailure) {
    return <ErrorState error={failure} onRetry={() => void summaryQuery.refetch()} />
  }

  if (!ready || summary === undefined) return null

  return (
    <main className="report-print">
      <header className="report-print__header">
        <div className="card-label">Print view</div>
        <h1 style={{ margin: 0 }}>Analytics report</h1>
        <div className="report-print__meta">
          <span>{period}</span>
          <span>{departmentLabel}</span>
          <span>{formatRangeLabel(from, to)}</span>
        </div>
      </header>

      <section className="report-print-card">
        <div className="metric-grid report-print__metrics">
          <Metric label="Avg efficiency" value={formatCount(summary.avgEfficiency)} unit="/100" footnote="from the selected scope" />
          <Metric label="Avg complexity" value={formatDecimal(summary.avgComplexity)} footnote="weighted task load" />
          <Metric label="On-time rate" value={formatRate(summary.onTimeRate)} footnote="closed tasks only" />
          <Metric label="Returned for correction" value={formatRate(summary.returnedForCorrection)} footnote="submitted tasks" />
        </div>
      </section>

      <section className="report-print-card">
        <div className="row-between" style={{ marginBottom: 'var(--o-space-3)' }}>
          <div>
            <div className="card-label">People</div>
            <h2 style={{ margin: 0 }}>Ranking by efficiency</h2>
          </div>
          <div style={{ color: 'var(--fg-muted)' }}>Top person is shown in the trend summary below.</div>
        </div>
        <div className="data-table-scroll report-print-table-scroll">
          <table className="data-table report-print-table">
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col">Department</th>
                <th scope="col" className="numeric">Tasks</th>
                <th scope="col" className="numeric">Score</th>
                <th scope="col">Status</th>
                <th scope="col" className="numeric">On-time</th>
                <th scope="col" className="numeric">First-time pass</th>
                <th scope="col" className="numeric">Avg complexity</th>
              </tr>
            </thead>
            <tbody>
              {people.map((row) => (
                <tr key={row.userId}>
                  <td>{row.name}</td>
                  <td>{departments.find((departmentRow) => departmentRow.departmentId === row.departmentId)?.name ?? 'No department'}</td>
                  <td className="numeric">{formatCount(row.tasks)}</td>
                  <td className="numeric">{row.score === null ? 'Not enough data' : formatCount(row.score)}</td>
                  <td>{tierLabel(row)}</td>
                  <td className="numeric">{formatRate(row.onTime)}</td>
                  <td className="numeric">{formatRate(row.firstTimePass)}</td>
                  <td className="numeric">{formatDecimal(row.avgComplexity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="report-print-card">
        <div className="row-between" style={{ marginBottom: 'var(--o-space-3)' }}>
          <div>
            <div className="card-label">Departments</div>
            <h2 style={{ margin: 0 }}>Scores by department</h2>
          </div>
          <div style={{ color: 'var(--fg-muted)' }}>Rows keep table borders in print and in jsdom.</div>
        </div>
        <div className="data-table-scroll report-print-table-scroll">
          <table className="data-table report-print-table">
            <thead>
              <tr>
                <th scope="col">Department</th>
                <th scope="col" className="numeric">Score</th>
                <th scope="col" className="numeric">On-time</th>
                <th scope="col" className="numeric">First-time pass</th>
              </tr>
            </thead>
            <tbody>
              {departments.map((row) => (
                <tr key={row.departmentId}>
                  <td>{row.name}</td>
                  <td className="numeric">{row.score === null ? 'Not enough data' : formatCount(row.score)}</td>
                  <td className="numeric">{formatRate(row.onTime)}</td>
                  <td className="numeric">{formatRate(row.firstTimePass)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="report-print-card">
        <div className="row-between" style={{ marginBottom: 'var(--o-space-3)' }}>
          <div>
            <div className="card-label">Trend</div>
            <h2 style={{ margin: 0 }}>{selectedPerson?.name ?? 'No person selected'}</h2>
          </div>
          <div style={{ color: 'var(--fg-muted)' }}>Derived from the same range as the analytics screen.</div>
        </div>
        <div className="data-table-scroll report-print-table-scroll">
          <table className="data-table report-print-table">
            <thead>
              <tr>
                <th scope="col">Range</th>
                <th scope="col" className="numeric">Score</th>
                <th scope="col" className="numeric">On-time</th>
                <th scope="col" className="numeric">First-time pass</th>
              </tr>
            </thead>
            <tbody>
              {trend.map((point) => (
                <tr key={point.from}>
                  <td>{formatRangeLabel(point.from, point.to)}</td>
                  <td className="numeric">{point.score === null ? 'Not enough data' : formatCount(point.score)}</td>
                  <td className="numeric">{formatRate(point.onTime)}</td>
                  <td className="numeric">{formatRate(point.firstTimePass)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  )
}

function PersonReport({
  userId,
  from,
  to,
  bucket,
}: {
  readonly userId: string
  readonly from: string
  readonly to: string
  readonly bucket: 'week' | 'month'
}) {
  const { api } = useAuth()

  const peopleQuery = useQuery({
    queryKey: ['reports-print-person-people', userId, from, to],
    queryFn: () => api.get<PersonRow[]>(`/api/analytics/people?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  })

  const person = useMemo(
    () => peopleQuery.data?.find((row) => row.userId === userId) ?? null,
    [peopleQuery.data, userId],
  )

  const tasksQuery = useQuery({
    queryKey: ['reports-print-person-tasks', userId, from, to],
    queryFn: () =>
      api.get<TaskListPage>(
        `/api/tasks?assigneeId=${encodeURIComponent(userId)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
  })

  const trendQuery = useQuery({
    queryKey: ['reports-print-person-trend', userId, from, to, bucket],
    enabled: person !== null,
    queryFn: () =>
      api.get<TrendResponse>(
        `/api/analytics/trend?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&bucket=${bucket}&assigneeId=${encodeURIComponent(userId)}`,
      ),
  })

  const ready = peopleQuery.isSuccess && tasksQuery.isSuccess && (person === null || trendQuery.isSuccess)

  const printed = useRef(false)
  useEffect(() => {
    if (!ready || printed.current) return
    printed.current = true
    window.print()
  }, [ready])

  const failure = peopleQuery.error ?? tasksQuery.error ?? trendQuery.error
  const hasFailure = peopleQuery.isError || tasksQuery.isError || trendQuery.isError
  const tasks = tasksQuery.data?.items ?? []
  const trend = trendQuery.data?.points ?? []

  if (peopleQuery.isPending || tasksQuery.isPending || trendQuery.isPending) {
    return <LoadingState label="Loading person report" />
  }

  if (hasFailure) {
    return <ErrorState error={failure} onRetry={() => void peopleQuery.refetch()} />
  }

  if (person === null) {
    return <ErrorState error={new Error('Person not found')} onRetry={() => void peopleQuery.refetch()} />
  }

  return (
    <main className="report-print">
      <header className="report-print__header">
        <div className="card-label">Print view</div>
        <h1 style={{ margin: 0 }}>{person.name}</h1>
        <div className="report-print__meta">
          <span>Person report</span>
          <span>{formatRangeLabel(from, to)}</span>
        </div>
      </header>

      <section className="report-print-card">
        <div className="metric-grid report-print__metrics">
          <Metric label="Efficiency" value={formatCount(person.score)} unit="/100" footnote="from the selected period" />
          <Metric label="Tasks" value={formatCount(person.tasks)} footnote="closed tasks in scope" />
          <Metric label="On-time" value={formatRate(person.onTime)} footnote="complexity-weighted" />
          <Metric label="First-time pass" value={formatRate(person.firstTimePass)} footnote="complexity-weighted" />
        </div>
      </section>

      <section className="report-print-card">
        <div className="row-between" style={{ marginBottom: 'var(--o-space-3)' }}>
          <div>
            <div className="card-label">Tasks</div>
            <h2 style={{ margin: 0 }}>Task history</h2>
          </div>
          <div style={{ color: 'var(--fg-muted)' }}>The person report keeps the detailed task list close to the summary.</div>
        </div>
        <div className="data-table-scroll report-print-table-scroll">
          <table className="data-table report-print-table">
            <thead>
              <tr>
                <th scope="col">Reference</th>
                <th scope="col">Title</th>
                <th scope="col">Status</th>
                <th scope="col">Deadline</th>
                <th scope="col" className="numeric">On-time</th>
                <th scope="col" className="numeric">Returns</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((row) => {
                const personTask = row as PersonTaskRow
                const firstSubmitted = personTask.firstSubmittedAt ?? null
                const onTime = firstSubmitted === null ? null : Date.parse(firstSubmitted) <= Date.parse(personTask.effectiveDeadlineAt)
                return (
                  <tr key={row.id}>
                    <td>{row.reference}</td>
                    <td>{row.title}</td>
                    <td>{row.status}</td>
                    <td>{formatDate(row.deadlineAt)}</td>
                    <td className="numeric">{onTime === null ? '—' : onTime ? 'On time' : 'Late'}</td>
                    <td className="numeric">{personTask.returnCount ?? 0}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="report-print-card">
        <div className="row-between" style={{ marginBottom: 'var(--o-space-3)' }}>
          <div>
            <div className="card-label">Trend</div>
            <h2 style={{ margin: 0 }}>Efficiency over time</h2>
          </div>
          <div style={{ color: 'var(--fg-muted)' }}>Derived from the same range as the analytics screen.</div>
        </div>
        <div className="data-table-scroll report-print-table-scroll">
          <table className="data-table report-print-table">
            <thead>
              <tr>
                <th scope="col">Range</th>
                <th scope="col" className="numeric">Score</th>
                <th scope="col" className="numeric">On-time</th>
                <th scope="col" className="numeric">First-time pass</th>
              </tr>
            </thead>
            <tbody>
              {trend.map((point) => (
                <tr key={point.from}>
                  <td>{formatRangeLabel(point.from, point.to)}</td>
                  <td className="numeric">{point.score === null ? 'Not enough data' : formatCount(point.score)}</td>
                  <td className="numeric">{formatRate(point.onTime)}</td>
                  <td className="numeric">{formatRate(point.firstTimePass)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  )
}

export function Print() {
  usePageTitle('Report')
  const [searchParams] = useSearchParams()
  const userId = searchParams.get('userId')
  const period = (searchParams.get('period') ?? 'quarter') as PeriodLabel
  const fromParam = searchParams.get('from')
  const toParam = searchParams.get('to')
  const range = useMemo(() => {
    if (fromParam !== null && toParam !== null) {
      return {
        from: fromParam,
        to: toParam,
        bucket: bucketForRange(fromParam, toParam),
      }
    }
    return resolvePeriod(period)
  }, [fromParam, period, toParam])

  if (userId !== null) {
    return <PersonReport userId={userId} from={range.from} to={range.to} bucket={range.bucket} />
  }

  return <TeamReport />
}
